// src/components/AboutPage.js
import React from "react";

const SkillPill = ({ name, level }) => (
  <div className="skill-pill mb-2 me-2">
    <div className="d-flex align-items-center">
      <strong className="me-2">{name}</strong>
      <div className="progress flex-grow-1" style={{height: "8px"}}>
        <div
          className="progress-bar"
          role="progressbar"
          style={{ width: `${level}%` }}
          aria-valuenow={level}
          aria-valuemin="0"
          aria-valuemax="100"
        />
      </div>
      <small className="ms-2 text-muted">{level}%</small>
    </div>
  </div>
);

const TeamMember = ({ img, name, role }) => (
  <div className="col-6 col-md-3 mb-3">
    <div className="text-center">
      <div className="team-photo rounded-circle mx-auto mb-2 overflow-hidden">
        <img src={img} alt={name} className="img-fluid" />
      </div>
      <h6 className="mb-0">{name}</h6>
      <small className="text-muted">{role}</small>
    </div>
  </div>
);

export default function AboutPage() {
  return (
    <main className="about-page">
      {/* Hero */}
      <section className="about-hero py-5 bg-light">
        <div className="container">
          <div className="row align-items-center gy-3">
            <div className="col-lg-7">
              <h1 className="fw-bold">SWSAA</h1>
              <p className="lead text-muted">
                We’re a small, focused team building reliable websites and quality testing solutions for small and medium businesses.
              </p>
              <p className="text-muted">
                We value clarity, maintainability and strong engineering practices — from clean UI to robust testing pipelines.
              </p>
              <div className="d-flex gap-2 mt-3">
                <a href="#contact" className="btn btn-primary rounded-pill">Work with us</a>
                <a href="#services" className="btn btn-outline-primary rounded-pill">Our Services</a>
              </div>
            </div>

            <div className="col-lg-5">
              <div className="card shadow-sm border-0">
                <img
                  src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=80"
                  alt="team working"
                  className="img-fluid rounded"
                />
                <div className="card-body small text-muted">
                  Photo: real collaboration — your partner in digital projects.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-5">
        <div className="container">
          <div className="row gy-4 align-items-center">
            <div className="col-md-6">
              <h3 className="fw-bold">Our Mission</h3>
              <p className="text-muted">
                To empower small businesses with professional websites and reliable testing so they can focus on growth. We combine practical design with rigorous QA.
              </p>

              <ul className="list-unstyled text-muted">
                <li className="mb-2"><i className="bi bi-check2-circle text-primary me-2"></i> Honest pricing and clear deliverables</li>
                <li className="mb-2"><i className="bi bi-check2-circle text-primary me-2"></i> Automated testing and CI/CD by default</li>
                <li className="mb-2"><i className="bi bi-check2-circle text-primary me-2"></i> Privacy and accessibility focused</li>
              </ul>
            </div>

            <div className="col-md-6">
              <h5 className="mb-3">Quick facts</h5>
              <div className="row">
                <div className="col-6 mb-3">
                  <div className="stat-card p-3 rounded shadow-sm text-center">
                    <h5 className="mb-0">+50</h5>
                    <small className="text-muted">Projects</small>
                  </div>
                </div>
                <div className="col-6 mb-3">
                  <div className="stat-card p-3 rounded shadow-sm text-center">
                    <h5 className="mb-0">4.9★</h5>
                    <small className="text-muted">Avg. rating</small>
                  </div>
                </div>
                <div className="col-6 mb-3">
                  <div className="stat-card p-3 rounded shadow-sm text-center">
                    <h5 className="mb-0">2 yrs</h5>
                    <small className="text-muted">Avg. engagement</small>
                  </div>
                </div>
                <div className="col-6 mb-3">
                  <div className="stat-card p-3 rounded shadow-sm text-center">
                    <h5 className="mb-0">Innsbruck</h5>
                    <small className="text-muted">Headquarters</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills / Tech */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="section-head text-center mb-4">
            <h3 className="fw-bold">What we use</h3>
            <p className="text-muted mb-0">Tools and technologies we rely on to deliver great results.</p>
          </div>

          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="d-flex flex-wrap align-items-center mb-3">
                <SkillPill name="React" level={90} />
                <SkillPill name="Bootstrap" level={95} />
                <SkillPill name="Cypress / Playwright" level={80} />
                <SkillPill name="Node / Express" level={75} />
                <SkillPill name="Docker & CI/CD" level={70} />
              </div>

              <p className="text-muted small">
                We choose pragmatic stacks that speed up development while keeping maintenance affordable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline / history */}
      <section className="py-5">
        <div className="container">
          <div className="section-head text-center mb-4">
            <h3 className="fw-bold">Our Journey</h3>
            <p className="text-muted mb-0">From a freelancer to a small team focused on quality solutions.</p>
          </div>

          <div className="timeline mt-4">
            <div className="timeline-item">
              <div className="timeline-dot" />
              <div className="timeline-content">
                <h6 className="mb-1">2019 — Freelance beginnings</h6>
                <p className="small text-muted mb-0">Started building landing pages and small business sites.</p>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-dot" />
              <div className="timeline-content">
                <h6 className="mb-1">2021 — Focus on testing</h6>
                <p className="small text-muted mb-0">Added automated testing services and CI pipelines for clients.</p>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-dot" />
              <div className="timeline-content">
                <h6 className="mb-1">2024 — Small team</h6>
                <p className="small text-muted mb-0">Formed a small team and expanded service offerings.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      {/* <section className="py-5 bg-light">
        <div className="container">
          <div className="section-head text-center mb-4">
            <h3 className="fw-bold">Meet the team</h3>
            <p className="text-muted mb-0">Small, experienced, and dedicated.</p>
          </div>

          <div className="row">
            <TeamMember
              img="https://images.unsplash.com/photo-1544723795-3fb6469f5b39?auto=format&fit=crop&w=800&q=80"
              name="Muhammad Asif"
              role="Founder / Lead Developer"
            />
            <TeamMember
              img="https://images.unsplash.com/photo-1545996124-7bfa2a1b2b7d?auto=format&fit=crop&w=800&q=80"
              name="Anna Müller"
              role="QA Engineer"
            />
            <TeamMember
              img="https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=800&q=80"
              name="Lukas Richter"
              role="Frontend Developer"
            />
            <TeamMember
              img="https://images.unsplash.com/photo-1547425260-76bcadfb4f2c?auto=format&fit=crop&w=800&q=80"
              name="Petra Novak"
              role="Project Manager"
            />
          </div>
        </div>
      </section> */}

      {/* CTA */}
      <section className="py-5">
        <div className="container text-center">
          <h4 className="fw-bold">Want to collaborate?</h4>
          <p className="text-muted small mb-3">Tell us about your idea and we’ll prepare a proposal.</p>
          <a href="#contact" className="btn btn-lg btn-primary rounded-pill">Request a Quote</a>
        </div>
      </section>
    </main>
  );
}
